import React, { useEffect, useMemo } from 'react';
import { injectIntl } from 'react-intl';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory, {
  PaginationProvider,
} from 'react-bootstrap-table2-paginator';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  getHandlerTableChange,
  NoRecordsFoundMessage,
  PleaseWaitMessage,
  sortCaret,
  headerSortingClasses,
} from '../../../../../_metronic/_helpers';
import { Pagination } from '../../../../../_metronic/_partials/controls';
import * as actions from '../../_redux/sales/salesActions';
import { useSalesUIContext } from './../SalesUIContext';
import * as uiHelpers from './../SalesUIHelpers';
import * as columnFormatters from './column-formatters';

const SalesTable = (props) => {
  const { intl } = props;
  const saleCodeLabel = intl.formatMessage({ id: 'SALES.LABEL.SALECODE' });
  const saleDateLabel = intl.formatMessage({ id: 'SALES.LABEL.SALEDATE' });
  const saleTypeLabel = intl.formatMessage({ id: 'SALES.LABEL.SALETYPE' });
  const fbNameLabel = intl.formatMessage({ id: 'SALES.LABEL.FBNAME' });
  const mobileLabel = intl.formatMessage({ id: 'SALES.LABEL.MOBILE' });
  const itemCountLabel = intl.formatMessage({ id: 'SALES.LABEL.ITEMCOUNT' });
  const totalAmountLabel = intl.formatMessage({
    id: 'SALES.LABEL.TOTALAMOUNT',
  });
  const statusLabel = intl.formatMessage({ id: 'SALES.LABEL.STATUS' });
  const actionsLabel = intl.formatMessage({ id: 'SALES.LABEL.ACTIONS' });

  // Sales UI Context
  const SalesUIContext = useSalesUIContext();
  const SalesUIProps = useMemo(() => {
    return {
      queryParams: SalesUIContext.queryParams,
      setQueryParams: SalesUIContext.setQueryParams,
      openViewSaleDialog: SalesUIContext.openViewSaleDialog,
      openDeleteSaleDialog: SalesUIContext.openDeleteSaleDialog,
    };
  }, [SalesUIContext]);

  // Getting curret state of Sales list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.sales }),
    shallowEqual
  );
  //console.log('currentState:', currentState);
  const { totalCount, entities, listLoading } = currentState
    ? currentState
    : { totalCount: 0, entities: [], listLoading: false };

  // Sales Redux state
  const dispatch = useDispatch();
  useEffect(() => {
    // server call by queryParams
    dispatch(actions.fetchSales(SalesUIProps.queryParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SalesUIProps.queryParams, dispatch]);

  // Table columns
  const columns = [
    {
      dataField: 'id',
      text: saleCodeLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
    },
    {
      dataField: 'saleDate',
      text: saleDateLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      formatter: columnFormatters.DateColumnFormatter,
    },
    {
      dataField: 'saleType',
      text: saleTypeLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      formatter: columnFormatters.SaleTypeColumnFormatter,
    },
    {
      dataField: 'fbName',
      text: fbNameLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
    },
    {
      dataField: 'mobile',
      text: mobileLabel,
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: 'itemCount',
      text: itemCountLabel,
      sort: true,
      sortCaret: sortCaret,
      classes: 'text-center',
    },
    {
      dataField: 'totalAmount',
      text: totalAmountLabel,
      sort: true,
      sortCaret: sortCaret,
      formatter: columnFormatters.PriceColumnFormatter,
    },
    {
      dataField: 'status',
      text: statusLabel,
      sort: true,
      sortCaret: sortCaret,
      formatter: columnFormatters.StatusColumnFormatter,
    },
    {
      dataField: 'action',
      text: actionsLabel,
      formatter: columnFormatters.ActionsColumnFormatter,
      formatExtraData: {
        openViewSaleDialog: SalesUIProps.openViewSaleDialog,
        openDeleteSaleDialog: SalesUIProps.openDeleteSaleDialog,
      },
      classes: 'text-right pr-0',
      style: {
        minWidth: '100px',
      },
    },
  ];
  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: SalesUIProps.queryParams.pageSize,
    page: SalesUIProps.queryParams.pageNumber,
  };
  return (
    <>
      <PaginationProvider pagination={paginationFactory(paginationOptions)}>
        {({ paginationProps, paginationTableProps }) => {
          return (
            <Pagination
              isLoading={listLoading}
              paginationProps={paginationProps}
            >
              <BootstrapTable
                wrapperClasses='table-responsive'
                bordered={true}
                classes='table table-head-custom table-vertical-center overflow-hidden'
                bootstrap4
                remote
                keyField='id'
                data={entities === null ? [] : entities}
                columns={columns}
                defaultSorted={uiHelpers.defaultSorted}
                onTableChange={getHandlerTableChange(
                  SalesUIProps.setQueryParams
                )}
                useSortBy
                {...paginationTableProps}
              >
                <PleaseWaitMessage entities={entities} />
                <NoRecordsFoundMessage entities={entities} />
              </BootstrapTable>
            </Pagination>
          );
        }}
      </PaginationProvider>
    </>
  );
};

export default injectIntl(SalesTable);
